"use client";

import { useQuery } from "@tanstack/react-query";
import { Music2 } from "lucide-react";
import { useState } from "react";
import { getMyProfileSongs } from "@/actions/music";
import { LiquidActionButton } from "@/components/ui/liquid-action-button";
import { cn } from "@/lib/utils";
import { MusicPickerDrawer, musicKeys } from "./music-picker-drawer";

/** The signed-in member's own songs, shared by every entry point. */
export function useMyProfileSongs(enabled = true) {
  return useQuery({
    queryKey: musicKeys.mine(),
    queryFn: async () => {
      const result = await getMyProfileSongs();
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    enabled,
    staleTime: 60_000,
  });
}

/**
 * The picker, preloaded with the member's current songs. For entry points
 * that don't already hold them (Control Center, Edit profile).
 */
export function MyMusicPickerDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mine = useMyProfileSongs(open);
  return (
    <MusicPickerDrawer
      open={open && mine.isSuccess}
      onOpenChange={onOpenChange}
      initialSongs={mine.data?.songs ?? []}
    />
  );
}

/**
 * The owner's optional way in, beside "About me": a small liquid-glass
 * button. Only rendered on your own profile and only while you have no songs
 * (with songs, the Music picks section has its own Edit).
 */
export function AddMusicLiquidButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <LiquidActionButton
        type="button"
        onClick={() => setOpen(true)}
        className={cn("h-8 rounded-full px-3.5", className)}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-black tracking-wider text-white uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          <Music2 aria-hidden className="size-3.5" strokeWidth={2.6} />
          Add music
        </span>
      </LiquidActionButton>
      <MusicPickerDrawer open={open} onOpenChange={setOpen} initialSongs={[]} />
    </>
  );
}
